import { useEffect, useState } from "react";
import API from "../../services/api";
import { TextField, MenuItem } from "@mui/material";
import {
    LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
    PieChart, Pie, Cell, Legend, ResponsiveContainer,
    BarChart, Bar
} from "recharts";
import { FaPhone, FaRupeeSign, FaCheckCircle, FaTimesCircle, FaClock, FaTools } from "react-icons/fa";
import { FaArrowUp, FaArrowDown } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { useLocation } from "react-router-dom";

function ServiceDashboard() {
    const loggedInUser =
        JSON.parse(localStorage.getItem("user"));

    const userRole =
        (loggedInUser?.role || "").toLowerCase();

    const loggedEmployeeId =
        loggedInUser?.empId ||
        loggedInUser?.employeeId ||
        loggedInUser?.EmployeeId ||
        loggedInUser?.empID ||
        "";

    const loggedBranchId =
        loggedInUser?.branchId ||
        loggedInUser?.BranchId ||
        "";

    const allBranches =
        (loggedInUser?.allBranches || "N") === "Y";

    const branchManager =
        (loggedInUser?.branchManager || "N") === "Y";

    const isAdmin =
        userRole === "admin";

    const isManager =
        userRole === "manager";

    const isUser =
        userRole === "user";

    // ✅ ALL STATES FIRST
    const location = useLocation();
    const navState = location.state;
    const params = new URLSearchParams(location.search);
    const [data, setData] = useState(null);
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");
    const [employee, setEmployee] = useState("");

    useEffect(() => {

        // USER -> auto select own name
        if (isUser) {

            setEmployee(
                loggedEmployeeId || ""
            );
        }

    }, [isUser, loggedInUser]);

    const [status, setStatus] = useState("");
    const [employees, setEmployees] = useState([]);
    const [hoveredCard, setHoveredCard] = useState(null);
    const [trend, setTrend] = useState({});
    const navigate = useNavigate();

    const completionRate = data?.totalServices
        ? ((data.completedCount / data.totalServices) * 100).toFixed(1)
        : 0;

    const avgRevenue = data?.totalServices
        ? Math.round(data.totalRevenue / data.totalServices)
        : 0;

    // ✅ API CALL
    const loadData = async (
        f = fromDate,
        t = toDate,
        emp = employee,
        stat = status,
        serviceStat = "",
        serviceType = ""
    ) => {

        // 🔹 helper to build query
        const buildQuery = (from, to, empVal, statVal, serviceStatVal, serviceTypeVal) => {
            let q = [];

            if (from) q.push(`fromDate=${from}`);
            if (to) q.push(`toDate=${to}`);
            if (empVal) q.push(`employee=${empVal}`);
            if (statVal) q.push(`status=${statVal}`);
            if (serviceStatVal) q.push(`serviceStatus=${serviceStatVal}`);
            if (serviceTypeVal) q.push(`serviceType=${serviceTypeVal}`);

            // Role information
            q.push(`role=${userRole}`);
            q.push(`branchId=${loggedBranchId}`);
            q.push(`branchManager=${branchManager ? "Y" : "N"}`);
            q.push(`empId=${loggedEmployeeId || ""}`);
            q.push(`allBranches=${allBranches ? "Y" : "N"}`);

            return q.join("&");
        };

        // 🔹 current data
        const currentUrl = `/dashboard/daily-service?${buildQuery(f, t, emp, stat, serviceStat, serviceType)}`;
        const currentRes = await API.get(currentUrl);
        setData(currentRes.data);

        // 🔹 calculate previous range
        const fromDateObj = new Date(f);
        const toDateObj = new Date(t);

        const diffDays = Math.ceil((toDateObj - fromDateObj) / (1000 * 60 * 60 * 24));

        const prevFrom = new Date(fromDateObj);
        prevFrom.setDate(prevFrom.getDate() - diffDays - 1);

        const prevTo = new Date(fromDateObj);
        prevTo.setDate(prevTo.getDate() - 1);

        const format = d => d.toISOString().split("T")[0];

        // 🔹 previous data
        const prevUrl = `/dashboard/daily-service?${buildQuery(
            format(prevFrom),
            format(prevTo),
            emp,
            stat,
            serviceStat,
            serviceType
        )}`;
        const prevRes = await API.get(prevUrl);

        // 🔹 trend calculator
        const calc = (c, p) =>
            (!p || p === 0)
                ? { value: 0, isUp: true }
                : { value: ((c - p) / p) * 100, isUp: c >= p };

        const cur = currentRes.data;
        const prev = prevRes.data;

        setTrend({
            totalServices: calc(cur.totalServices, prev.totalServices),
            totalRevenue: calc(cur.totalRevenue, prev.totalRevenue),
            completed: calc(cur.completedCount, prev.completedCount),
            closed: calc(cur.closedCount, prev.closedCount),
            pending: calc(cur.pendingCount, prev.pendingCount)
        });
    };

    useEffect(() => {

        console.log("RAW URL:", location.search);

        const params = new URLSearchParams(location.search);

        const from = params.get("fromDate");
        const to = params.get("toDate");
        const emp = params.get("employee");
        const stat = params.get("status");
        const serviceStatus = params.get("serviceStatus");
        const serviceType = params.get("serviceType");

        if (from && to) {

            // 🔥 Drill-down case
            setFromDate(from);
            setToDate(to);
            setEmployee(emp || "");
            setStatus(stat || "");

            // 🔥 IMPORTANT: pass ALL filters
            loadData(
                from,
                to,
                emp || "",
                stat || "",
                serviceStatus || "",
                serviceType || ""
            );

        } else {

            // 🔥 DEFAULT (LAST 7 DAYS)
            const today = new Date();
            const last7 = new Date();
            last7.setDate(today.getDate() - 7);

            const format = d => d.toLocaleDateString("en-CA");

            const fromDateVal = format(last7);
            const toDateVal = format(today);

            setFromDate(fromDateVal);
            setToDate(toDateVal);

            const employeeFilter =
                isUser
                    ? (loggedEmployeeId || "")
                    : "";

            loadData(
                fromDateVal,
                toDateVal,
                employeeFilter,
                "",
                "",
                ""
            );
        }

        // 🔥 dropdown (you can later change to service dropdown API)
        API.get("/DailyCallReport/dropdowns-all")
            .then(res => {

                let employeeData = res.data.employees || [];

                // ADMIN + AllBranches=Y
                if (isAdmin && allBranches) {

                    // show all
                }

                // ADMIN + AllBranches=N
                else if (isAdmin && !allBranches) {

                    employeeData = employeeData.filter(
                        e => String(e.branchId) === String(loggedBranchId)
                    );
                }

                // MANAGER + BranchManager=Y
                else if (isManager && branchManager) {

                    employeeData = employeeData.filter(
                        e => String(e.branchId) === String(loggedBranchId)
                    );
                }

                // MANAGER + BranchManager=N
                else if (isManager && !branchManager) {

                    employeeData = employeeData.filter(
                        e => String(e.id) === String(loggedEmployeeId)
                    );
                }

                // USER
                else if (isUser) {

                    employeeData = employeeData.filter(
                        e => String(e.id) === String(loggedEmployeeId)
                    );
                }

                setEmployees(employeeData);
            });

    }, [location.search]);

    const handleLast7Days = () => {
        const today = new Date();
        const last7 = new Date();
        last7.setDate(today.getDate() - 7);

        const format = d => d.toLocaleDateString("en-CA");

        const from = format(last7);
        const to = format(today);

        setFromDate(from);
        setToDate(to);

        loadData(
            from,
            to,
            isUser ? (loggedEmployeeId || "") : "",
            status,
            "",
            ""
        );
    };

    if (!data) return <div>Loading...</div>;

    const lineData = (data.servicesPerDay || []).map(x => ({
        date: x.date,
        count: x.count
    }));

    const normalizeEmpName = (value) =>
        (value || "")
            .toString()
            .trim()
            .toLowerCase()
            .replace(/\s+/g, " ");

    const employeeData = (data.servicesByEmployee || []).map(x => {
        const rawEmployeeId =
            x.employeeId ??
            x.EmployeeId ??
            x.empId ??
            x.EmpId ??
            x.id ??
            x.Id ??
            "";

        const empName =
            (
                x.employee ??
                x.Employee ??
                x.employeeName ??
                x.EmployeeName ??
                x.empName ??
                x.EmpName ??
                x.name ??
                x.Name ??
                ""
            ).toString().trim();

        const empNameNorm = normalizeEmpName(empName);

        const matchedEmployee = employees.find(e => {
            const masterNameNorm = normalizeEmpName(e.empName);

            if (!masterNameNorm || !empNameNorm) return false;

            return (
                String(e.id) === String(rawEmployeeId || "") ||
                masterNameNorm === empNameNorm ||
                empNameNorm.includes(masterNameNorm) ||
                masterNameNorm.includes(empNameNorm)
            );
        });

        const finalEmployeeId =
            rawEmployeeId && Number(rawEmployeeId) > 0
                ? rawEmployeeId
                : matchedEmployee?.id || "";

        return {
            name: empName,
            employeeId: finalEmployeeId,
            employeeName: empName,
            count: x.count,
            revenue: Math.round(
                (data.totalServices ? (data.totalRevenue / data.totalServices) : 0) * x.count
            )
        };
    });

    const isSingleEmployee =
        employeeData.length === 1;

    const pieData = (data.serviceTypeDistribution || []).map(x => ({
        name: x.serviceType,
        value: x.count
    }));

    const renderTrend = (t) => {
        if (!t) return null;

        return (
            <div style={{
                ...styles.trend,
                color: t.isUp ? "#2ed573" : "#ff4757"
            }}>
                {t.isUp ? <FaArrowUp size={10} /> : <FaArrowDown size={10} />}
                {Math.abs(t.value).toFixed(1)}%
            </div>
        );
    };

    const formatDateForApi = (value) => {
        if (!value) return "";

        if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}/.test(value)) {
            return value.substring(0, 10);
        }

        const d = new Date(value);

        if (isNaN(d.getTime())) {
            return "";
        }

        return d.toLocaleDateString("en-CA");
    };

    const handleDrillDown = (extraFilters = {}) => {

        const params = new URLSearchParams();

        const finalFrom = extraFilters.fromDate ?? fromDate;
        const finalTo = extraFilters.toDate ?? toDate;

        const apiFrom = formatDateForApi(finalFrom);
        const apiTo = formatDateForApi(finalTo);

        console.log("DRILL:", apiFrom, apiTo, extraFilters);

        if (apiFrom) {
            params.set("fromDate", apiFrom);
        }

        if (apiTo) {
            params.set("toDate", apiTo);
        }

        const selectedEmployee = extraFilters.employee ?? employee;
        const selectedEmployeeName = extraFilters.employeeName || "";

        if (selectedEmployee && Number(selectedEmployee) > 0) {
            params.set("employee", selectedEmployee);
        }
        else if (selectedEmployeeName) {
            params.set("employeeName", selectedEmployeeName);
        }

        if (extraFilters.status) {
            params.set("status", extraFilters.status);
        }

        if (extraFilters.serviceStatus) {
            params.set("serviceStatus", extraFilters.serviceStatus);
        }

        if (extraFilters.serviceType) {
            params.set("serviceType", extraFilters.serviceType);
        }

        // ✅ send role info to ServiceReport also
        params.set("role", userRole);
        params.set("branchId", loggedBranchId || "");
        params.set("branchManager", branchManager ? "Y" : "N");
        params.set("empId", loggedEmployeeId || "");
        params.set("allBranches", allBranches ? "Y" : "N");

        navigate(`/app/service-report?${params.toString()}`);
    };

    const handleEmployeeBarClick = (barData) => {
        if (!barData || !barData.payload) return;

        handleDrillDown({
            employee: barData.payload.employeeId,
            employeeName: barData.payload.employeeName || barData.payload.name
        });
    };

    return (
        <div style={styles.container}>

            <div style={styles.header}>
                <h2>Daily Service - Dashboard</h2>
            </div>

            <div style={styles.filterRow}>
                <input
                    type="date"
                    value={fromDate}
                    onChange={e => setFromDate(e.target.value)}
                    style={styles.input}
                />

                <input
                    type="date"
                    value={toDate}
                    onChange={e => setToDate(e.target.value)}
                    style={styles.input}
                />

                <TextField
                    select
                    label="Employee"
                    value={employee}
                    disabled={isUser}
                    onChange={(e) => setEmployee(e.target.value)}
                    size="small"
                    sx={{
                        ...styles.inputStyle,
                        minWidth: "370px"
                    }}
                >
                    {!isUser && (
                        <MenuItem value="" sx={styles.menuItem}>
                            All
                        </MenuItem>
                    )}

                    {[...employees]

                        .sort((a, b) =>
                            (a.empName || "").localeCompare(b.empName || "")
                        )

                        .map(emp => (

                            <MenuItem
                                key={emp.id}
                                value={emp.id}
                                sx={styles.menuItem}
                            >
                                <div style={{
                                    display: "flex",
                                    width: "100%",
                                    alignItems: "center"
                                }}>

                                    {/* EMPLOYEE */}
                                    <span style={{
                                        width: "160px",
                                        overflow: "hidden",
                                        whiteSpace: "nowrap",
                                        textOverflow: "ellipsis"
                                    }}>
                                        {emp.empName}
                                    </span>

                                    {/* BRANCH */}
                                    <span style={{
                                        width: "140px"
                                    }}>
                                        {"-> "} {emp.branchName}
                                    </span>

                                    {/* MOBILE */}
                                    <span>
                                        {"-> "} {emp.mobileNo}
                                    </span>

                                </div>
                            </MenuItem>

                        ))}
                </TextField>

                <TextField
                    select
                    label="Status"
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    size="small"
                    sx={styles.inputStyle}
                >
                    <MenuItem value="" sx={styles.menuItem}>All</MenuItem>
                    <MenuItem value="Active" sx={styles.menuItem}>Active</MenuItem>
                    <MenuItem value="Closed" sx={styles.menuItem}>Closed</MenuItem>
                    <MenuItem value="Pending" sx={styles.menuItem}>Pending</MenuItem>
                </TextField>

                <button style={styles.filterBtn} onClick={() => loadData()}>
                    Filter
                </button>

                <button style={styles.last7Btn} onClick={handleLast7Days}>
                    Last 7 Days
                </button>
            </div>

            <div style={styles.cardRow}>

                {/* TOTAL SERVICES */}
                <div
                    style={{
                        ...styles.cardBase,
                        ...styles.cardTotal,
                        ...(hoveredCard === "total" && styles.cardHover)
                    }}
                    onMouseEnter={() => setHoveredCard("total")}
                    onMouseLeave={() => setHoveredCard(null)}
                    onClick={() => {
                        const avg = data.totalRevenue / (data.totalServices || 1);
                        const threshold = Math.round(avg);

                        handleDrillDown({});
                    }}
                >
                    <div style={styles.cardContent}>
                        <FaTools size={16} />
                        <h3 style={styles.cardTitle}>Total Services</h3>
                    </div>

                    <p style={styles.cardValueText}>{data.totalServices}</p>
                    {renderTrend(trend.totalServices)}
                </div>

                {/* TOTAL REVENUE */}
                <div
                    style={{
                        ...styles.cardBase,
                        ...styles.cardValue,
                        ...(hoveredCard === "value" && styles.cardHover)
                    }}
                    onMouseEnter={() => setHoveredCard("value")}
                    onMouseLeave={() => setHoveredCard(null)}
                    onClick={() => {
                        const avg = data.totalRevenue / (data.totalServices || 1);
                        const threshold = Math.round(avg);

                        handleDrillDown({});
                    }}
                >
                    <div style={styles.cardContent}>
                        <FaRupeeSign size={16} />
                        <h3 style={styles.cardTitle}>Total Revenue</h3>
                    </div>

                    <p style={styles.cardValueText}>
                        ₹ {(data.totalRevenue || 0).toLocaleString("en-IN")}
                    </p>
                    {renderTrend(trend.totalRevenue)}
                </div>

                {/* AVG REVENUE */}
                <div
                    style={{
                        ...styles.cardBase,
                        background: "linear-gradient(135deg, #3742fa, #1e90ff)",
                        ...(hoveredCard === "avg" && styles.cardHover)
                    }}
                    onMouseEnter={() => setHoveredCard("avg")}
                    onMouseLeave={() => setHoveredCard(null)}
                >
                    <div style={styles.cardContent}>
                        <FaRupeeSign size={16} />
                        <h3 style={styles.cardTitle}>Avg Revenue</h3>
                    </div>

                    <p style={styles.cardValueText}>₹ {avgRevenue}</p>
                </div>

                {/* COMPLETED */}
                <div
                    style={{
                        ...styles.cardBase,
                        ...styles.cardActive,
                        ...(hoveredCard === "completed" && styles.cardHover)
                    }}
                    onMouseEnter={() => setHoveredCard("completed")}
                    onMouseLeave={() => setHoveredCard(null)}
                    onClick={() => handleDrillDown({ serviceStatus: "Completed" })}
                >
                    <div style={styles.cardContent}>
                        <FaCheckCircle size={16} />
                        <h3 style={styles.cardTitle}>Completed</h3>
                    </div>

                    <p style={styles.cardValueText}>{data.completedCount}</p>
                    {renderTrend(trend.completed)}
                </div>

                {/* CLOSED */}
                <div
                    style={{
                        ...styles.cardBase,
                        ...styles.cardClosed,
                        ...(hoveredCard === "closed" && styles.cardHover)
                    }}
                    onMouseEnter={() => setHoveredCard("closed")}
                    onMouseLeave={() => setHoveredCard(null)}
                    onClick={() => handleDrillDown({ status: "Closed" })}
                >
                    <div style={styles.cardContent}>
                        <FaTimesCircle size={16} />
                        <h3 style={styles.cardTitle}>Closed</h3>
                    </div>

                    <p style={styles.cardValueText}>{data.closedCount}</p>
                    {renderTrend(trend.closed)}
                </div>

                {/* PENDING */}
                <div
                    style={{
                        ...styles.cardBase,
                        ...styles.cardPending,
                        ...(hoveredCard === "pending" && styles.cardHover)
                    }}
                    onMouseEnter={() => setHoveredCard("pending")}
                    onMouseLeave={() => setHoveredCard(null)}
                    onClick={() => handleDrillDown({ serviceStatus: "Pending" })}
                >
                    <div style={styles.cardContent}>
                        <FaClock size={16} />
                        <h3 style={styles.cardTitle}>Pending</h3>
                    </div>

                    <p style={styles.cardValueText}>{data.pendingCount}</p>
                    {renderTrend(trend.pending)}
                </div>

                {/* COMPLETION % */}
                <div
                    style={{
                        ...styles.cardBase,
                        background: "linear-gradient(135deg, #ffa502, #ff7f50)",
                        ...(hoveredCard === "completion" && styles.cardHover)
                    }}
                    onMouseEnter={() => setHoveredCard("completion")}
                    onMouseLeave={() => setHoveredCard(null)}
                >
                    <div style={styles.cardContent}>
                        <FaCheckCircle size={16} />
                        <h3 style={styles.cardTitle}>Completion %</h3>
                    </div>

                    <p style={styles.cardValueText}>{completionRate}%</p>
                </div>
            </div>

            <div style={styles.chartRow}>

                {!isSingleEmployee ? (

                    // Employee comparison chart

                    <div style={styles.chartBox}>
                        <h3>Services by Employee</h3>

                        <ResponsiveContainer width="100%" height={320}>
                            <BarChart data={employeeData}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="name" />
                                <YAxis />
                                <Tooltip />
                                <Bar
                                    dataKey="count"
                                    fill="#1e90ff"
                                    onClick={handleEmployeeBarClick}
                                />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>

                ) : (

                    // Single employee / user login → show date-wise bar graph

                    <div style={styles.chartBox}>
                        <h3>Services by Date</h3>

                        <ResponsiveContainer width="100%" height={320}>
                            <BarChart data={lineData}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis
                                    dataKey="date"
                                    tickFormatter={(d) => new Date(d).toLocaleDateString()}
                                />
                                <YAxis />
                                <Tooltip />
                                <Bar
                                    dataKey="count"
                                    fill="#1e90ff"
                                    onClick={(data) => {
                                        if (!data || !data.payload) return;

                                        const clickedDate = formatDateForApi(data.payload.date);

                                        if (!clickedDate) {
                                            console.log("Invalid date bar click:", data.payload);
                                            return;
                                        }

                                        handleDrillDown({
                                            fromDate: clickedDate,
                                            toDate: clickedDate
                                        });
                                    }}
                                />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>

                )}

                <div style={styles.chartBox}>
                    <h3>Revenue by Employee</h3>

                    <ResponsiveContainer width="100%" height={320}>
                        <BarChart data={employeeData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="name" />
                            <YAxis />
                            <Tooltip />
                            <Bar
                                dataKey="revenue"
                                fill="#2ed573"
                                onClick={handleEmployeeBarClick}
                            />
                        </BarChart>
                    </ResponsiveContainer>
                </div>

                <div style={styles.chartBox}>
                    <h3>Service Type Distribution</h3>

                    <ResponsiveContainer width="100%" height={320}>
                        <PieChart margin={{ top: 10, right: 20, left: 20, bottom: 40 }}>
                            <Pie
                                data={pieData}
                                dataKey="value"
                                nameKey="name"
                                outerRadius={100}
                                onClick={(data) => {
                                    handleDrillDown({ serviceType: data.name });
                                }}
                                label={({ percent }) =>
                                    percent > 0.05 ? `${(percent * 100).toFixed(0)}%` : ""
                                }
                            >
                                {pieData.map((entry, index) => (
                                    <Cell key={index} fill={`hsl(${index * 40},70%,60%)`} />
                                ))}
                            </Pie>
                            <Tooltip />
                            <Legend verticalAlign="bottom" height={50} />
                        </PieChart>
                    </ResponsiveContainer>
                </div>

            </div>
        </div>
    );
}

const styles = {
    container: {
        padding: "10px",
        fontFamily: "Verdana",
        fontSize: "12px"
    },
    header: {
        background: "#2f3542",
        color: "#fff",
        padding: "6px",
        borderRadius: "6px",
        marginBottom: "10px"
    },
    filterRow: {
        display: "flex",
        flexWrap: "wrap",
        gap: "10px",
        alignItems: "center",
        marginBottom: "10px"
    },
    input: {
        padding: "6px",
        borderRadius: "4px",
        border: "1px solid #ccc"
    },
    filterBtn: {
        background: "#2ed573",
        border: "none",
        padding: "6px 12px",
        color: "#fff",
        borderRadius: "4px",
        cursor: "pointer"
    },
    last7Btn: {
        background: "#1e90ff",
        border: "none",
        padding: "6px 12px",
        color: "#fff",
        borderRadius: "4px",
        cursor: "pointer"
    },
    cardRow: {
        display: "flex",
        gap: "10px",
        marginTop: "10px"
    },
    card: {
        background: "#fff",
        padding: "10px",
        borderRadius: "6px",
        boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
        flex: "1 1 150px",
        textAlign: "center"
    },
    chartRow: {
        display: "flex",
        gap: "20px",
        marginTop: "20px"
    },
    chartBox: {
        background: "#fff",
        padding: "15px",
        borderRadius: "6px",
        boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
        flex: "1 1 400px",
        minWidth: "300px"
    },
    inputStyle: {
        minWidth: "140px",
        flex: "1 1 150px",
        "& .MuiOutlinedInput-root": {
            height: "32px",
            fontSize: "12px",
            fontFamily: "Verdana"
        },
        "& .MuiInputBase-input": {
            padding: "8px 10px",
            fontFamily: "Verdana",
            fontSize: "12px"
        },
        "& .MuiInputLabel-root": {
            fontFamily: "Verdana",
            fontSize: "12px"
        }
    },
    menuItem: {
        fontFamily: "Verdana",
        fontSize: "12px"
    },
    container: {
        padding: "10px",
        fontFamily: "Verdana",
        fontSize: "12px",
        maxWidth: "100%",
        boxSizing: "border-box"
    },
    cardBase: {
        padding: "18px 12px",
        borderRadius: "8px",
        boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
        flex: "1 1 150px",
        textAlign: "center",
        color: "#fff",
        cursor: "pointer",
        transition: "all 0.2s ease"
    },

    cardValue: {
        background: "#2ed573"
    },

    cardActive: {
        background: "#1e90ff"
    },

    cardClosed: {
        background: "#747d8c"
    },

    cardPending: {
        background: "#ff4757"
    },
    cardTitle: {
        margin: 0,
        fontSize: "12px",
        fontWeight: "500"
    },

    cardValueText: {
        margin: 0,
        fontSize: "15px",
        fontWeight: "bold"
    },
    cardContent: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "6px",
        marginBottom: "4px"
    },
    cardHover: {
        transform: "translateY(-4px)",
        boxShadow: "0 8px 18px rgba(0,0,0,0.2)"
    },
    cardTotal: {
        background: "linear-gradient(135deg, #57606f, #2f3542)"
    },

    cardValue: {
        background: "linear-gradient(135deg, #2ed573, #1eae60)"
    },

    cardActive: {
        background: "linear-gradient(135deg, #1e90ff, #3742fa)"
    },

    cardClosed: {
        background: "linear-gradient(135deg, #747d8c, #57606f)"
    },

    cardPending: {
        background: "linear-gradient(135deg, #ff6b81, #ff4757)"
    },
    trend: {
        fontSize: "11px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "3px",
        marginTop: "3px",
        fontWeight: "bold"
    },
};

export default ServiceDashboard;